/**
 * Where one run of an ingestion rule's trigger on a file stands. Mirrors the
 * SDK `IngestionRunStatus`.
 *
 * - `running`: the ingestion action is queued or running.
 * - `failed`: the action failed or was cancelled, or the trigger could not start it.
 * - `finished`: the action finished; the file's `ingestion_status` says how much was ingested.
 */
export type IngestionRunStatus = "running" | "failed" | "finished";

/** The latest time an ingestion rule's trigger ran on a file. */
export interface IngestionRun {
  file_id: string;
  rule_id: string;
  trigger_id: string;
  started: string;
  status: IngestionRunStatus;
  /** Null when the trigger could not start an invocation. */
  invocation_id: string | null;
  /** Why the trigger could not start the action, when it could not. */
  detail: string | null;
}

/** What `POST /v1/ingestion-rules/ingest` did with one file. */
export type IngestFileOutcome =
  | "started"
  | "already_running"
  | "no_matching_rule"
  | "auto_ingest_off"
  | "not_started"
  | "not_found";

/** What to tell the user each outcome of ingesting a file means. */
export const INGEST_FILE_OUTCOME_MESSAGES: Record<IngestFileOutcome, string> = {
  started: "Ingestion started.",
  already_running: "Roboto is already ingesting this file.",
  no_matching_rule:
    "No ingestion rule matches this file now, so nothing was started.",
  auto_ingest_off:
    "Auto-ingest is off on the rule that matches this file, so nothing was started.",
  not_started: "Roboto couldn’t start the ingestion action. Try again later.",
  not_found:
    "Roboto couldn’t find this file, or you don’t have permission to edit it.",
};

export interface IngestFileResult {
  file_id: string;
  outcome: IngestFileOutcome;
  rule_id: string | null;
  invocation_id: string | null;
}

/**
 * Files already uploaded that a rule matches, that are not ingested, and that
 * no rule's trigger has run on: uploaded before the rule existed, or while its
 * auto-ingest was off.
 */
export interface ExistingFilesPreview {
  count: number;
  /** Counting stops after a fixed number of files. */
  count_is_lower_bound: boolean;
  /** Up to 10 of the files' paths. */
  sample_paths: string[];
  /**
   * Complete sessions holding any of these files. Each is fully ingested again
   * after its files are ingested, which starts its session triggers again.
   */
  complete_session_count: number;
}

/** The most files one `ingestFiles` call takes; the server's cap. */
export const MAX_INGEST_FILES = 100;
